import * as DG from "ui/dialogs"
import { ReactNode } from "react"

import Button from "ui/buttons/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import create from "zustand"
import { css } from "@emotion/css"
import { useI18n } from "providers/I18n"

type ViewInfo = {
  title?: string
  description?: ReactNode
  cancelLabel?: string
  submitLabel?: string
  type?: "primary" | "primary_destructive" | "secondary_destructive"
}

type ActionInfo = {
  action: () => Promise<unknown>
  onSuccess?: () => void
  onError?: (e: Error) => void
  pending?: boolean
}

type ExtraActionInfo = ActionInfo & {
  label: string
  type?: "primary" | "primary_destructive" | "secondary_destructive"
}

type ConfirmationState = {
  state: "OPEN" | "CLOSED"
  viewInfo: ViewInfo
  clear: () => void
  actionInfo: ActionInfo | null
  extraActionInfo: ExtraActionInfo | null
  setPending: (p: boolean, action: "actionInfo" | "extraActionInfo") => void

  confirm: (ai: ActionInfo, vi: ViewInfo, ei?: ExtraActionInfo) => void
}

const empty_info: ViewInfo = {}

const useConfirmationStore = create<ConfirmationState>((set, get) => ({
  //
  state: "CLOSED",
  viewInfo: empty_info,
  actionInfo: null,
  extraActionInfo: null,
  clear: () => {
    if (get().actionInfo?.pending || get().extraActionInfo?.pending) return
    set((s) => ({
      ...s,
      state: "CLOSED",
      info: empty_info,
      actionInfo: null,
      extraActionInfo: null,
    }))
  },

  setPending: (
    p: boolean,
    action: "actionInfo" | "extraActionInfo" = "actionInfo",
  ) => {
    set((s) => ({
      ...s,
      [action]: { ...s[action], pending: p },
    }))
  },

  confirm: (ai: ActionInfo, vi: ViewInfo, ei?: ExtraActionInfo) => {
    set((s) => ({
      ...s,
      actionInfo: ai,
      extraActionInfo: ei || s.extraActionInfo,
      viewInfo: vi,
      state: "OPEN",
    }))
  },
}))

export function useConfirmation() {
  return useConfirmationStore((s) => s.confirm)
}

//#region View shit

function chooseVariant(type: ViewInfo["type"]): "outlined" | "contained" {
  switch (type) {
    case "primary":
    case "primary_destructive":
      return "contained"
    case "secondary_destructive":
      return "outlined"
    default:
      return "contained"
  }
}

function chooseColor(type: ViewInfo["type"]): "primary" | "destructive" {
  switch (type) {
    case "primary":
      return "primary"
    case "primary_destructive":
      return "destructive"
    case "secondary_destructive":
      return "destructive"
    default:
      return "primary"
  }
}

const styles = {
  content: css`
    padding: var(--spacing-xs) 0 var(--spacing-xl) var(--spacing-xl);
  `,
  contentText: css`
    font-size: 14px;
    color: var(--dark-grey);
  `,
  progress: css`
    color: currentColor;
  `,
}

const id = "confirmation-dialog-title"
export function Confirmation() {
  const { t } = useI18n()
  const { state, viewInfo, clear, actionInfo, extraActionInfo, setPending } =
    useConfirmationStore()

  const onCancel = clear
  const handleSubmit = (
    ai: ActionInfo | null = actionInfo,
    key: "actionInfo" | "extraActionInfo" = "actionInfo",
  ) => {
    if (ai === null) return
    setPending(true, key)
    const { action, onSuccess, onError } = ai
    action()
      .then(() => {
        setPending(false, key)
        onSuccess?.()
        clear()
      })
      .catch((e: Error) => {
        setPending(false, key)
        onError?.(e)
        clear()
      })
  }

  return (
    <DG.Dialog open={state === "OPEN"} onClose={onCancel} id={id} maxWidth="sm">
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>
          {viewInfo.title ?? t("confirmation.dialog.title")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={onCancel} />
      </DG.DialogHeader>

      <DG.DialogContent className={styles.content}>
        <p className={styles.contentText}>
          {viewInfo.description ?? t("confirmation.dialog.description")}
        </p>
      </DG.DialogContent>

      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={onCancel}>
            {viewInfo.cancelLabel ?? t("confirmation.dialog.cancel")}
          </Button>
          <Button
            variant={chooseVariant(viewInfo.type)}
            color={chooseColor(viewInfo.type)}
            onClick={() => handleSubmit()}
            startIcon={
              actionInfo?.pending && (
                <CircularProgress size={18} className={styles.progress} />
              )
            }
          >
            {viewInfo.submitLabel ?? t("confirmation.dialog.submit")}
          </Button>
          {extraActionInfo ? (
            <Button
              variant={chooseVariant(extraActionInfo.type)}
              color={chooseColor(extraActionInfo.type)}
              onClick={() => handleSubmit(extraActionInfo, "extraActionInfo")}
              startIcon={
                extraActionInfo.pending && (
                  <CircularProgress size={18} className={styles.progress} />
                )
              }
            >
              {extraActionInfo.label}
            </Button>
          ) : null}
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

//#endregion
